// PWA-Install-Flow: pure Entscheidungs-Logik + beforeinstallprompt-Capture.
//
// Die Plattform-Entscheidung ist bewusst eine pure Funktion über Flags —
// so bleibt sie ohne DOM testbar. Die Browser-Verdrahtung (Standalone-/
// iOS-Detection) lebt in notifications.ts (isIOS/isPWAStandalone) und wird
// hier nur zusammengesteckt (detectInstallPlatform).
//
// beforeinstallprompt MUSS vor dem React-Mount abgefangen werden (Chrome
// feuert es genau einmal, oft bevor irgendeine Komponente lauscht) —
// deshalb ruft main.tsx setupPwaInstallCapture() im Modul-Scope auf.

import { isIOS, isPWAStandalone } from './notifications'

export type InstallPlatform = 'ios' | 'android' | 'none'

/** localStorage-Key für den Dismiss-Timestamp (ms since epoch). */
export const DISMISS_KEY = 'ember.pwa-install-dismissed'
/** sessionStorage-Key: Auto-Anzeige max. 1× pro Tab-Session. */
const AUTO_SHOWN_KEY = 'ember.pwa-install-autoshown'
/** Nach „Später" so viele Tage nicht automatisch wieder aufpoppen. */
export const DEFAULT_SNOOZE_DAYS = 14

export interface InstallPlatformInput {
  /** Läuft bereits als installierte App (display-mode standalone / navigator.standalone). */
  isStandalone: boolean
  isIOS: boolean
  /** Ein beforeinstallprompt-Event wurde abgefangen und wartet (Chromium/Android). */
  hasBeforeInstallPrompt: boolean
  /** Phone-Breakpoint (< sm) — Desktop bleibt komplett unberührt. */
  isPhone: boolean
}

/**
 * Welche Install-UI passt zur Umgebung?
 * - standalone oder Desktop → 'none' (nichts anzeigen)
 * - iOS-Phone im Browser-Tab → 'ios' (Anleitung; iOS feuert nie beforeinstallprompt)
 * - Phone mit wartendem beforeinstallprompt → 'android' (nativer Prompt)
 * - sonst 'none' (z.B. Android-Browser ohne Install-Support)
 */
export function getInstallPlatform(input: InstallPlatformInput): InstallPlatform {
  if (input.isStandalone || !input.isPhone) return 'none'
  if (input.isIOS) return 'ios'
  if (input.hasBeforeInstallPrompt) return 'android'
  return 'none'
}

export interface ShouldShowInput {
  platform: InstallPlatform
  /** Timestamp (ms) des letzten Dismiss — null wenn nie dismissed. */
  dismissedAt: number | null
  now: number
  snoozeDays?: number
}

/**
 * Auto-Anzeige nur wenn die Plattform passt und ein früheres „Später"
 * älter als die Snooze-Frist ist (bzw. es nie eines gab). Kaputte
 * Timestamps (NaN aus localStorage) zählen wie „nie dismissed".
 */
export function shouldShowInstallSheet(input: ShouldShowInput): boolean {
  if (input.platform === 'none') return false
  if (input.dismissedAt === null || !Number.isFinite(input.dismissedAt)) return true
  const snoozeMs = (input.snoozeDays ?? DEFAULT_SNOOZE_DAYS) * 86_400_000
  return input.now - input.dismissedAt >= snoozeMs
}

/**
 * Marker-Check: iOS-Dritt-Browser bzw. In-App-Browser (Mail, Instagram,
 * Facebook, Google-App, …) — dort fehlt der Teilen→Home-Bildschirm-Flow,
 * das Sheet weist dann dezent auf Safari hin. Pure Funktion über den
 * UA-String; der Aufrufer kombiniert mit isIOS().
 */
export function isNonSafariIOSBrowser(ua: string): boolean {
  return /CriOS|FxiOS|EdgiOS|OPiOS|OPT\/|GSA\/|FBAN|FBAV|Instagram|DuckDuckGo|Line\//.test(ua)
}

// ---------------------------------------------------------------------------
// Browser-Verdrahtung (ab hier nichts Pures mehr)
// ---------------------------------------------------------------------------

/** Chromiums beforeinstallprompt — nicht in lib.dom.d.ts, daher minimal typisiert. */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
}

let deferredPrompt: BeforeInstallPromptEvent | null = null
const promptListeners = new Set<() => void>()

function notifyPromptChange(): void {
  for (const cb of promptListeners) cb()
}

/**
 * Global beforeinstallprompt/appinstalled abfangen. In main.tsx im
 * Modul-Scope aufrufen — VOR dem React-Mount, sonst verpasst man das Event.
 * Idempotent genug für StrictMode nicht nötig (Modul-Scope = 1×).
 */
export function setupPwaInstallCapture(): void {
  if (typeof window === 'undefined') return
  window.addEventListener('beforeinstallprompt', (e) => {
    // Chromiums Mini-Infobar unterdrücken — wir zeigen unser eigenes Sheet.
    e.preventDefault()
    deferredPrompt = e as BeforeInstallPromptEvent
    notifyPromptChange()
  })
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null
    notifyPromptChange()
  })
}

export function hasDeferredInstallPrompt(): boolean {
  return deferredPrompt !== null
}

/** Für useSyncExternalStore — feuert wenn ein Prompt ankommt/verbraucht wird. */
export function subscribeInstallPromptChange(cb: () => void): () => void {
  promptListeners.add(cb)
  return () => {
    promptListeners.delete(cb)
  }
}

/**
 * Den gespeicherten nativen Install-Prompt zeigen. Ein Event ist nur einmal
 * prompt()-bar — danach wird es verworfen (Chrome feuert bei Ablehnung
 * später ggf. ein frisches beforeinstallprompt).
 */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const evt = deferredPrompt
  if (!evt) return 'unavailable'
  deferredPrompt = null
  notifyPromptChange()
  try {
    await evt.prompt()
    const choice = await evt.userChoice
    return choice.outcome
  } catch {
    return 'unavailable'
  }
}

/** Aktuelle Plattform aus der echten Browser-Umgebung (isPhone kommt vom Hook). */
export function detectInstallPlatform(isPhone: boolean): InstallPlatform {
  return getInstallPlatform({
    isStandalone: isPWAStandalone(),
    isIOS: isIOS(),
    hasBeforeInstallPrompt: hasDeferredInstallPrompt(),
    isPhone,
  })
}

export function getDismissedAt(): number | null {
  try {
    const raw = window.localStorage.getItem(DISMISS_KEY)
    if (raw === null) return null
    return Number(raw)
  } catch {
    return null
  }
}

export function markInstallSheetDismissed(now: number = Date.now()): void {
  try {
    window.localStorage.setItem(DISMISS_KEY, String(now))
  } catch {
    // Quota/Private-Mode — dann poppt es halt nächste Session wieder auf.
  }
}

/** Auto-Anzeige max. 1× pro Tab-Session (überlebt SPA-Navigation + Reload). */
export function wasAutoShownThisSession(): boolean {
  try {
    return window.sessionStorage.getItem(AUTO_SHOWN_KEY) === '1'
  } catch {
    return false
  }
}

export function markAutoShownThisSession(): void {
  try {
    window.sessionStorage.setItem(AUTO_SHOWN_KEY, '1')
  } catch {
    // ignore
  }
}
